import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type CardsResponse, CardStatus } from '@shared/api/rest/cards';
import { CardItem } from '../cardGridViewItem';
import { NoData, Spinner, Tabs } from '@shared/widgets';

interface Props {
  className?: string;
  loading?: boolean;
  cards: CardsResponse[];
  total: number;
  activeTab?: CardStatus;
  setActiveTab: (status?: CardStatus) => void;
}

export const RelatedCards = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('user');

  const statusOptions = useMemo(
    () => [
      { label: t('statuses.card.All', { ns: 'common' }), value: undefined },
      ...Object.entries(CardStatus).map(([value, label]) => ({ label: t(`statuses.card.${label}`, { ns: 'common' }), value })),
    ],
    [t],
  );
  return (
    <div className={cn(props.className)}>
      <Tabs
        className={cn('w-full', PaletteUtils.isVcfcPalette && 'rounded-t-xl bg-white px-6 py-4 dark:bg-soft-black')}
        listClassName={cn(PaletteUtils.isInsigniaPalette && 'gap-0', PaletteUtils.isVcfcPalette && 'gap-12')}
        labelClassName={cn(PaletteUtils.isInsigniaPalette && 'pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black')}
        options={statusOptions}
        value={props.activeTab}
        onChange={(value) => props.setActiveTab(value as CardStatus)}
      />
      {props.loading && <Spinner className={cn('bg-white dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'rounded-b-xl py-5')} />}
      {!props.loading && props.cards.length > 0 && (
        <div
          className={cn(
            'rounded-tr-2 rounded-br-2 rounded-bl-2 flex w-full flex-wrap rounded-tl-none bg-white pt-5 dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && ['rounded-b-xl rounded-t-none', 'max-md:justify-center'],
          )}
        >
          {props.cards.map((card, idx) => {
            const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === card.cardId)?.viewCardDetails;
            return (
              <Link
                className='transition-transform duration-200 hover:scale-110'
                key={idx}
                to={RoutePaths.card.onNavigate(card.cardId)}
                onClick={(e) => viewCardDetails === false && e.preventDefault()}
              >
                <CardItem card={card} withHover />
              </Link>
            );
          })}
        </div>
      )}
      {!props.loading && props.cards.length === 0 && (
        <NoData
          className={cn('bg-white py-16 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'rounded-b-xl')}
          text={t('noData.noRecords', { ns: 'common' })}
        />
      )}
    </div>
  );
};
